import { Component, Input, OnInit, signal, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { LoaderService } from '@client/app/services/loader.service';
import { SecurityUserService } from '@client/app/services/security-user.service';
import { CommonPipesModule } from '@client/app/shared/common-pipes.module';
import { SharedModule } from '@client/app/shared/shared.module';
import { TranslateService } from '@client/app/translate/translate.service';
import { DsButtonVariants, DsInputStates, DsModule, Icons } from '@levelaccess/design-system';
import { $securityGroupUsers } from '@shared/constants/security-group';
import { $user } from '@shared/constants/user';
import { ISecurityUserWithGroups, ISecurityUserWithGroupsResponse } from '@shared/interfaces/security-group.interface';

export interface IRoleOption {
  id: string;
  name: string;
}

export interface IAddUserModalResult {
  users: ISecurityUserWithGroups[];
  groupId: string;
}

export enum FormFields {
  role = 'role',
  users = 'users',
}

interface ModalForm {
  [FormFields.role]: FormControl<string>;
  [FormFields.users]: FormControl<ISecurityUserWithGroups[]>;
}

@Component({
  selector: 'app-add-user-modal',
  templateUrl: './add-user-modal.component.html',
  styleUrl: './add-user-modal.component.scss',
  imports: [DsModule, CommonPipesModule, SharedModule, ReactiveFormsModule],
  standalone: true,
})
export class AddUserModalComponent implements OnInit {
  private readonly DROPDOWN_SIZE: number = 10;

  @Input({ required: true }) availableRoles: IRoleOption[] = [];
  @Input({ required: true }) tenantId: string;
  @Input() excludeGroupIds: string[] = [];

  form: FormGroup<ModalForm>;
  possibleUsers: ISecurityUserWithGroups[] = [];

  readonly validationError: WritableSignal<string> = signal('');

  protected readonly DsInputStates: typeof DsInputStates = DsInputStates;
  protected readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  protected readonly FormFields: typeof FormFields = FormFields;
  protected readonly Icons: typeof Icons = Icons;

  constructor(
    readonly modal: NgbActiveModal,
    private readonly securityUserService: SecurityUserService,
    private readonly translateService: TranslateService,
    private readonly loaderService: LoaderService,
  ) {}

  protected get selectedUsersCount(): number {
    return this.form.get(FormFields.users)?.value?.length ?? 0;
  }

  ngOnInit(): void {
    const defaultRole: string = this.availableRoles[0]?.id || '';
    const isSingleRole: boolean = this.availableRoles.length === 1;

    this.form = new FormGroup<ModalForm>({
      [FormFields.role]: new FormControl<string>(
        { value: defaultRole, disabled: isSingleRole },
        { nonNullable: true, validators: [Validators.required] },
      ),
      [FormFields.users]: new FormControl<ISecurityUserWithGroups[]>([], {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(1)],
      }),
    });
  }

  fetchUsers = (searchTerm: string): Observable<ISecurityUserWithGroups[]> => {
    if (!searchTerm || searchTerm.length < 2) {
      this.possibleUsers = [];
      return of<ISecurityUserWithGroups[]>([]);
    }

    const selectedUsers: ISecurityUserWithGroups[] = this.form.get(FormFields.users)?.value ?? [];
    const selectedUserIds: Set<string> = new Set<string>(selectedUsers.map((user: ISecurityUserWithGroups) => user[$user._id]));

    this.loaderService.skipNext();

    return this.securityUserService
      .findUsersWithSecurityGroupsByTenantId({
        searchTerm,
        limit: this.DROPDOWN_SIZE + selectedUsers.length,
        page: 0,
        tenantId: this.tenantId,
        isServiceGroup: false,
        isStaffGroup: false,
        excludeGroupIds: this.excludeGroupIds,
      })
      .pipe(
        map((response: ISecurityUserWithGroupsResponse): ISecurityUserWithGroups[] =>
          response[$securityGroupUsers.users].filter((user: ISecurityUserWithGroups) => !selectedUserIds.has(user[$user._id])),
        ),
        catchError(() => of<ISecurityUserWithGroups[]>([])),
        tap((users: ISecurityUserWithGroups[]) => {
          this.possibleUsers = users;
        }),
      );
  };

  protected valueToDropdownLabel = (user: ISecurityUserWithGroups): string => {
    return `${user[$user.displayName]} (${user[$user.email]})`;
  };

  protected valueToSelectedItemLabel = (user: ISecurityUserWithGroups): string => {
    return user[$user.email];
  };

  protected onCancel(): void {
    this.modal.dismiss();
  }

  protected onSubmit(): void {
    this.validationError.set('');

    const selectedUsers: ISecurityUserWithGroups[] = this.form.get(FormFields.users)?.value ?? [];
    if (this.form.invalid || selectedUsers.length === 0) {
      this.validationError.set(this.translateService.instant('add_at_least_one_user_to_continue'));
      return;
    }

    const result: IAddUserModalResult = {
      users: selectedUsers,
      groupId: this.form.getRawValue()[FormFields.role],
    };

    this.modal.close(result);
  }
}
